<template>
  <div
    :class="[
      'rounded-lg border border-gray-200 bg-white p-6',
      isDropTarget ? 'border-purple-400 bg-purple-50' : ''
    ]"
    :style="isDropTarget ? 'cursor: copy' : ''"
    @dragover="onDocumentGroupDragOver"
    @dragenter="onDocumentGroupDragEnter"
    @dragleave="onDocumentGroupDragLeave"
    @drop="onDocumentGroupDrop"
  >
    <DocumentGroupHeader
      :document="document"
      :document-index="documentIndex"
      :total-documents="totalDocuments"
      :original-document-state="originalDocumentState"
      :has-changes="hasChanges"
      :drag-over-document-group="dragOverDocumentGroup"
      :case-id="caseId"
      :page-rotations="pageRotations"
      @document-change="markAsChanged"
      @merge-documents="mergeDocumentsByIndex"
    />

    <div class="my-4 border-t border-gray-200"></div>

    <DocumentPageGrid
      :document="document"
      :document-index="documentIndex"
      :organized-documents="organizedDocuments"
      :drag-over-target="dragOverTarget"
      :drag-over-document-group="dragOverDocumentGroup"
      :is-dragging="isDragging"
      :drag-mode="dragMode"
      :drag-source-document="dragSourceDocument"
      :page-rotations="pageRotations"
      @document-change="markAsChanged"
      @split-document="(pageIndex: number) => splitDocument(documentIndex, pageIndex)"
      @open-add-documents="
        (pageIndex: number, side: 'left' | 'right') =>
          openAddDocumentsDialog(documentIndex, pageIndex, side)
      "
      @create-new-document="(pages: any[]) => handleCreateNewDocument(documentIndex, pages)"
      @move-pages="
        (pages: any[], targetDocName: string) =>
          handleMovePages(documentIndex, pages, targetDocName)
      "
      @delete-pages="(pages: any[]) => handleDeletePages(documentIndex, pages)"
      @drag-start="onDragStart"
      @drag-start-multiple="onDragStartMultiple"
      @drag-end="onDragEnd"
      @drag-over="onDragOver"
      @drop="onDrop"
      @drag-enter="onDragEnter"
      @drag-leave="onDragLeave"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import type { DocumentGroup } from './composables/useDocumentOrganization'
  import { useDocumentOrganizationContext } from './composables/useDocumentOrganization'
  import DocumentGroupHeader from './DocumentGroupHeader.vue'
  import DocumentPageGrid from './DocumentPageGrid.vue'

  interface Props {
    document: DocumentGroup
    documentIndex: number
  }

  const props = defineProps<Props>()

  // Get all state and methods from the provider context
  const {
    organizedDocuments,
    originalDocumentState,
    hasChanges,
    caseId,
    pageRotations,
    draggedPage,
    draggedPages,
    dragOverDocumentGroup,
    dragOverTarget,
    isDragging,
    dragMode,
    dragSourceDocument,
    markAsChanged,
    splitDocument,
    mergeDocumentsByIndex,
    openAddDocumentsDialog,
    handleCreateNewDocument,
    handleMovePages,
    handleDeletePages,
    handleGlobalDragStart,
    handleGlobalDragStartMultiple,
    handleGlobalDragEnd,
    handleGlobalDragPageMove,
    handleGlobalDragToGroup,
    handleGlobalDragOverGroup,
    handleGlobalDragEnterGroup,
    handleGlobalDragLeaveGroup,
    handleGlobalDragEnterTarget,
    handleGlobalDragLeaveTarget,
    updateMousePosition
  } = useDocumentOrganizationContext()

  // Computed properties
  const totalDocuments = computed(() => organizedDocuments.value.length)

  const isDropTarget = computed(() => dragOverDocumentGroup.value === props.documentIndex)

  // Drag event handlers - now directly use context methods
  const onDragStart = (event: DragEvent, docIndex: number, pageIndex: number) => {
    handleGlobalDragStart({ docIndex, pageIndex })
  }

  const onDragStartMultiple = (event: DragEvent, docIndex: number, pageUuids: string[]) => {
    handleGlobalDragStartMultiple({ docIndex, pageUuids })
  }

  const onDragEnd = () => {
    handleGlobalDragLeaveTarget()
    handleGlobalDragEnd()
  }

  const onDragOver = (event: DragEvent, docIndex: number, pageIndex: number) => {
    event.preventDefault()
    updateMousePosition(event)
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
  }

  const onDragEnter = (
    event: DragEvent,
    docIndex: number,
    pageIndex: number,
    dropZone?: 'left' | 'right'
  ) => {
    if (!draggedPage.value) return
    handleGlobalDragEnterTarget(docIndex, pageIndex, dropZone)
  }

  const onDragLeave = (event: DragEvent, docIndex: number, pageIndex: number) => {
    handleGlobalDragLeaveTarget()
  }

  const onDrop = (
    event: DragEvent,
    targetDocIndex: number,
    targetPageIndex: number,
    dropZone?: 'left' | 'right'
  ) => {
    event.preventDefault()

    if (!draggedPage.value) {
      handleGlobalDragEnd()
      return
    }

    const { docIndex: sourceDocIndex, pageIndex: sourcePageIndex } = draggedPage.value
    const targetDocument = organizedDocuments.value[targetDocIndex]

    // Calculate the actual target index based on drop zone
    let actualTargetIndex = targetPageIndex

    if (dropZone === 'right') {
      actualTargetIndex = targetPageIndex + 1
    }
    // For dropZone === 'left', we use targetPageIndex as is

    // Handle edge case: if dropping to the right of the last page,
    // ensure we don't exceed the document length
    if (actualTargetIndex > targetDocument.pages.length) {
      actualTargetIndex = targetDocument.pages.length
    }

    handleGlobalDragPageMove({
      sourceDocIndex,
      sourcePageIndex,
      targetDocIndex,
      targetPageIndex: actualTargetIndex
    })

    handleGlobalDragLeaveTarget()
    handleGlobalDragEnd()
  }

  const onDocumentGroupDragOver = (event: DragEvent) => {
    event.preventDefault()
    updateMousePosition(event)
    handleGlobalDragOverGroup(props.documentIndex)
  }

  const onDocumentGroupDragEnter = (event: DragEvent) => {
    event.preventDefault()
    if (event.target === event.currentTarget) {
      handleGlobalDragEnterGroup(props.documentIndex)
    }
  }

  const onDocumentGroupDragLeave = (event: DragEvent) => {
    event.preventDefault()
    // Only trigger if we're leaving the actual container, not moving to child elements
    if (event.target === event.currentTarget) {
      handleGlobalDragLeaveGroup()
    }
  }

  const onDocumentGroupDrop = (event: DragEvent) => {
    event.preventDefault()

    if (draggedPage.value && draggedPage.value.docIndex === props.documentIndex) {
      handleGlobalDragEnd()
      return
    }

    if (draggedPages.value && draggedPages.value.docIndex === props.documentIndex) {
      handleGlobalDragEnd()
      return
    }

    // Handle the drop using context methods
    if (draggedPages.value) {
      handleGlobalDragToGroup({
        sourceDocIndex: draggedPages.value.docIndex,
        pageUuids: draggedPages.value.pageUuids,
        targetDocIndex: props.documentIndex
      })
    } else if (draggedPage.value) {
      handleGlobalDragToGroup({
        sourceDocIndex: draggedPage.value.docIndex,
        sourcePageIndex: draggedPage.value.pageIndex,
        targetDocIndex: props.documentIndex
      })
    }

    // Always reset drag state after handling the drop
    handleGlobalDragEnd()
  }
</script>
